import React, { Fragment, useEffect } from 'react';
import { observer } from 'mobx-react';
import {
    CheckboxInput,
    FlexDiv,
    Heading2,
    Heading3,
    Heading4,
    TextDefault,
    TextLead, ThemedButton,
} from '@awarego-ui/components';
import RadioButton from '../../RadioButton';
import moment from 'moment/moment';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { IconButton, Typography } from '@mui/material';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import makeStyles from '@mui/styles/makeStyles';
import LaunchIcon from '@mui/icons-material/Launch';
import SettingMultiSelect from '../../../containers/settings/SettingMultiSelect';

const useStyles = makeStyles(() => ({
    authType: {
        display: 'flex',
        padding: '15px 0 40px 0',
        '& label.checked': {
            fontWeight: 700,
        },
        '& .container-radiobutton': {
            paddingLeft: 30,
            fontWeight: 600,
            fontSize: 16,
            lineHeight: '24px',
            marginRight: 20,
            '& .radiomark': {
                width: 22,
                height: 22,
                '&:after': {
                    left: 2,
                    top: 2,
                    width: 14,
                    height: 14,
                },
            },
        },
    },
}));
function Settings({
          authentication,
          dueDate,
          onChangeDueDate,
          onChangeAuthentication,
          adminTimeZone,
          emails_enabled,
          emailsForm,
          onChangeEmailsEnabled,
          customization_enabled,
          onChangeCustomizationEnabled,
          bannerImageSrc,
          bannerLinkURL,
          finishUrl,
          onChangeBannerImageSrc,
          onChangeBannerLinkURL,
          onChangeFinishUrl,
          createOptionsArray,
          enabledLanguages,
          setEnabledLanguages,
          })
    {
    const classes = useStyles();

    const [dueError, setDueError] = React.useState(null);
    const [bannerImageSrcError, setBannerImageSrcError] = React.useState(null);
    const [bannerLinkURLError, setBannerLinkURLError] = React.useState(null);
    const [finishUrlError, setFinishUrlError] = React.useState(null);

    const handleOnEmailDelete = (user) => {
        user.onDel();
    }

    const handleOnEmailAdd = async () => {
        const usersField = emailsForm.$('users');

        usersField.onAdd();

        // the new element is ALWAYS the last numeric key
        const addedIndex   = usersField.size - 1;   // size is computed in Base
        const newUserField = usersField.$(`${addedIndex}`);

        // silently mark it invalid (no red border yet)
        await newUserField.$('email')
            .validate({ showErrors: false });
    };

    useEffect(() => {
        if (!customization_enabled) {
            setBannerImageSrcError(null);
            setBannerLinkURLError(null);
            setFinishUrlError(null);
            return;
        }

        const REQ_MSG = 'Banner image URL is required when banner link URL is set';

        if (bannerImageSrc) {
            if (!bannerImageSrc.startsWith('https://')) {
                setBannerImageSrcError('Invalid url format');
            } else {
                setBannerImageSrcError(null);
            }
        } else {
            setBannerImageSrcError(bannerLinkURL ? REQ_MSG : null);
        }

        if (bannerLinkURL && !bannerLinkURL.startsWith('https://')) {
            setBannerLinkURLError('Invalid url format');
        } else {
            setBannerLinkURLError(null);
        }

        if (finishUrl && !finishUrl.startsWith('https://')) {
            setFinishUrlError('Invalid url format');
        } else {
            setFinishUrlError(null);
        }
    }, [bannerImageSrc, bannerLinkURL, finishUrl, customization_enabled]);

    const handleBannerImageSrcChange = (value) => {
        onChangeBannerImageSrc(value);
    };

    const handleBannerLinkUrlChange = (value) => {
        onChangeBannerLinkURL(value);
    };

    const handleFinishUrlChange = (value) => {
        onChangeFinishUrl(value);
    };


        const errorMessageDueDate = React.useMemo(() => {
        switch (dueError) {
            case 'maxDate':
            case 'disablePast': {
                return 'Due date cannot be set in the past';
            }

            case 'invalidDate': {
                return 'Your date is not valid';
            }

            default: {
                return '';
            }
        }
    }, [dueError]);

    return (
        <Fragment>
            <div>
                <br />
                <Heading2 $noWrap displayBlock>
                    Set authentication*
                </Heading2>
                <Heading4 lighter mt={8} displayBlock>
                    Type of authentication
                </Heading4>
                <div className={classes.authType}>
                    <Fragment>
                        <RadioButton
                            id="typeAuth1"
                            label="Requried Email"
                            value={'required-email'}
                            groupValue={authentication}
                            onClick={onChangeAuthentication}
                        />
                        <RadioButton
                            id="typeAuth0"
                            label="No email"
                            value={'no-email'}
                            groupValue={authentication}
                            onClick={onChangeAuthentication}
                        />
                        <RadioButton
                            id="typeAuth-1"
                            label="Optional email"
                            value={'optional-email'}
                            groupValue={authentication}
                            onClick={onChangeAuthentication}
                        />
                    </Fragment>
                </div>
                <FlexDiv column maxWidth={264}>
                    <Heading2 mt={20} $noWrap displayBlock>
                        Set due date*
                    </Heading2>
                    <Heading4 lighter mt={8} mb={5} displayBlock>
                        Date & time
                    </Heading4>
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                        <DateTimePicker
                            hiddenLabel={true}
                            value={dueDate ? moment(dueDate) : null}
                            onChange={onChangeDueDate}
                            ampm={false}
                            dayOfWeekFormatter={(day) => `${day}`}
                            disablePast={true}
                            format={`DD/MM/YYYY HH:mm[${adminTimeZone}]`}
                            views={['day', 'hours', 'minutes']}
                            onError={(newError) => setDueError(newError)}
                            slotProps={{
                                textField: {
                                    helperText: errorMessageDueDate,
                                    placeholder: 'Select date & time',
                                },
                            }}
                        />
                    </LocalizationProvider>
                </FlexDiv>
                <FlexDiv mt={40} alignItems="flex-start">
                    {/* Email Notifications Section */}
                    <FlexDiv column style={{ flex: '1 1 0', maxWidth: 264 }}>
                        <CheckboxInput
                            style={{height: '43px'}}
                            checked={emails_enabled}
                            onChange={onChangeEmailsEnabled}
                            name="emailNotifications"
                            label={<TextLead bold>Email(s) notifications</TextLead>}
                            noYPadding
                        />
                        {emails_enabled ? (
                            <Fragment>
                                {emailsForm.values().users.length > 0 && (
                                    <Heading3 displayBlock mt={16} mb={8}>
                                        Emails
                                    </Heading3>
                                )}
                                {emailsForm.$('users').map((user, i) => {
                                    return (
                                        <div key={user.id}>
                                            <div className="assessment-reminder-row">
                                                <div className="reminder-row-count">{i + 1}.</div>
                                                <div className={'d-flex flex-row'}>
                                                    <div className="form-holder" style={{width: '225px'}}>
                                                        <input
                                                            type="email"
                                                            name="recipientEmail"
                                                            className={
                                                                user.$('email').isValid
                                                                    ? 'bigInput dark'
                                                                    : 'bigInput '
                                                            }
                                                            {...user.$('email').bind()}
                                                        />
                                                        <div className="error">{user.$('email').error}</div>
                                                    </div>
                                                    <IconButton
                                                        aria-label="delete"
                                                        onClick={() => handleOnEmailDelete(user)}
                                                        color="secondary"
                                                        style={{width: '40px', height: '40px'}}
                                                    >
                                                        <DeleteForeverIcon />
                                                    </IconButton>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                                {emailsForm.values().users.length === 0 ? <br /> : ''}
                                {emailsForm.values().users.length < 3 && (
                                    <div>
                                        <TextDefault
                                            link
                                            themed
                                            bold
                                            onClick={handleOnEmailAdd}
                                        >
                                            + Add new email
                                        </TextDefault>
                                    </div>
                                )}
                            </Fragment>
                        ) : null}
                    </FlexDiv>
                    {/* Webhook Button */}
                    <FlexDiv alignItems="flex-start" style={{ flex: '0 0 auto' }}>
                        <div className="d-flex align-items-start">
                            <a href="/settings/webhooks" target="_blank" rel="noopener noreferrer">
                                <ThemedButton
                                    mode="secondary"
                                    text="Setup Webhooks"
                                    icon={<LaunchIcon fontSize="small" color="primary" />}
                                    iconPosition="end"
                                />
                            </a>
                        </div>
                    </FlexDiv>
                </FlexDiv>
                <FlexDiv column mt={45}>
                    <CheckboxInput
                        checked={customization_enabled}
                        onChange={onChangeCustomizationEnabled}
                        name="customization"
                        label={<TextLead bold>Enable Customization</TextLead>}
                        noYPadding
                    />
                    {customization_enabled ? (
                        <Fragment>
                            <Heading4 lighter mt={20} mb={5} displayBlock>
                                Banner image URL
                            </Heading4>
                            <div key='banner-img-src'>
                                <div className="assessment-reminder-row">
                                    <div className={'d-flex flex-row'}>
                                        <div style={{width: '550px'}}>
                                            <input
                                                type="url"
                                                value={bannerImageSrc}
                                                onChange={(e) => handleBannerImageSrcChange(e.target.value)}
                                                name="brandingImageSrc"
                                                placeholder="https://example.com/image.png"
                                                className={
                                                    bannerImageSrcError
                                                        ? 'bigInput dark'
                                                        : 'bigInput '
                                                }
                                            />
                                            <div className="error">{bannerImageSrcError}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <Heading4 lighter mt={8} mb={5} displayBlock>
                                Banner link URL
                            </Heading4>
                            <div key='banner-link-url'>
                                <div className="assessment-reminder-row">
                                    <div className={'d-flex flex-row'}>
                                        <div style={{width: '550px'}}>
                                            <input
                                                type="url"
                                                onChange={(e) => handleBannerLinkUrlChange(e.target.value)}
                                                value={bannerLinkURL}
                                                name="bannerLinkURL"
                                                placeholder="https://example.com"
                                                className={
                                                    bannerLinkURLError
                                                        ? 'bigInput dark'
                                                        : 'bigInput '
                                                }
                                            />
                                            <div className="error">{bannerLinkURLError}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <Heading4 lighter mt={8} mb={5} displayBlock>
                                Finish URL (Learn more about HRA button)
                            </Heading4>
                            <div key='finish-url'>
                                <div className="assessment-reminder-row">
                                    <div className={'d-flex flex-row'}>
                                        <div style={{width: '550px'}}>
                                            <input
                                                type="url"
                                                onChange={(e) => handleFinishUrlChange(e.target.value)}
                                                value={finishUrl}
                                                name="finishUrl"
                                                placeholder="https://example.com"
                                                className={
                                                    finishUrlError
                                                        ? 'bigInput dark'
                                                        : 'bigInput '
                                                }
                                            />
                                            <div className="error">{finishUrlError}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Fragment>
                    ) : null}
                </FlexDiv>
                <SettingMultiSelect
                    name="Languages"
                    options={createOptionsArray}
                    value={enabledLanguages}
                    noSavedMark={true}
                    py={20}
                    textLead={true}
                    action={(value) => (setEnabledLanguages(value))}
                />
            </div>
        </Fragment>
    );
}

export default observer(Settings);
